import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
  where,
} from "firebase/firestore";
import { auth, db } from "../../firebase";
import { optimizeImageForUpload } from "../../imageUploadUtils";
import { filterPersonalInfo } from "../../utils/chatFilter";
import {
  getConsultationJoinLabel,
  normalizeConsultationMode,
  openConsultationCallWindow,
} from "../../consultationUtils";
import "./ConsultationChat.css";

// CONSULTATION CHAT: Cloud Function endpoint for secure chat operations.
const CONSULTATION_CHAT_OPS_URL =
  process.env.REACT_APP_CONSULTATION_CHAT_OPS_URL ||
  "https://us-central1-petaar-3dcab.cloudfunctions.net/consultationChatOps";

// CONSULTATION CHAT: UI warning when personal contact details are blocked.
const BLOCK_WARNING_TEXT =
  "⚠️ Petaar blocked personal contact info to protect both parties.\nAll consultations must stay within Petaar.";
const IMAGE_MAX_DATA_URL_CHARS = 850000;

// CONSULTATION CHAT: Safe timestamp formatter for message metadata.
function formatMessageTime(ts) {
  if (!ts?.seconds) return "Sending...";
  return new Date(ts.seconds * 1000).toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });
}

// CONSULTATION CHAT: Relative time formatter for vet sidebar list.
function formatTimeAgo(ts) {
  if (!ts?.seconds) return "now";
  const diff = Date.now() - ts.seconds * 1000;
  const mins = Math.max(0, Math.floor(diff / 60000));
  if (mins < 1) return "now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

// CONSULTATION CHAT: Client-side data URL conversion for owner image messages.
function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(new Error("Unable to read image file"));
    reader.readAsDataURL(file);
  });
}

export default function ConsultationChat({
  bookingId,
  userRole,
  currentUserUid,
}) {
  const [messages, setMessages] = useState([]);
  const [chatDoc, setChatDoc] = useState(null);
  const [chatStatusDetails, setChatStatusDetails] = useState(null);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [closingConsultation, setClosingConsultation] = useState(false);
  const [joiningCall, setJoiningCall] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [composerWarning, setComposerWarning] = useState("");
  const [vetConsultations, setVetConsultations] = useState([]);
  const [showMobileList, setShowMobileList] = useState(false);
  const messagesEndRef = useRef(null);
  const imageInputRef = useRef(null);
  const readSyncInFlightRef = useRef(false);

  // CONSULTATION CHAT: Shared API caller with Firebase ID token auth.
  const callChatApi = useCallback(async (action, payload = {}) => {
    const token = await auth.currentUser?.getIdToken();
    if (!token) {
      throw new Error("Session expired. Please login again.");
    }
    const response = await fetch(CONSULTATION_CHAT_OPS_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        action,
        bookingId,
        ...payload,
      }),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || !data?.success) {
      throw new Error(data?.error || `Request failed (${response.status})`);
    }
    return data;
  }, [bookingId]);

  // CONSULTATION CHAT: Ensure chat document is initialized and load role-safe status snapshot.
  useEffect(() => {
    if (!bookingId) return;
    let active = true;
    const init = async () => {
      try {
        setLoadError("");
        await callChatApi("init_chat");
        const statusResult = await callChatApi("get_chat_status");
        if (active) {
          setChatStatusDetails(statusResult || null);
        }
      } catch (error) {
        if (active) {
          setLoadError(error.message || "Unable to load consultation chat.");
        }
      }
    };
    void init();
    return () => {
      active = false;
    };
  }, [bookingId, callChatApi]);

  // CONSULTATION CHAT: Realtime subscription to consultation chat metadata.
  useEffect(() => {
    if (!bookingId) {
      setChatDoc(null);
      return undefined;
    }
    const unsub = onSnapshot(
      doc(db, "consultationChats", bookingId),
      (snap) => {
        setChatDoc(snap.exists() ? { id: snap.id, ...snap.data() } : null);
      },
      (error) => {
        setLoadError(error.message || "Unable to read chat status.");
      },
    );
    return () => unsub();
  }, [bookingId]);

  // CONSULTATION CHAT: Realtime subscription to ordered consultation messages.
  useEffect(() => {
    if (!bookingId) {
      setMessages([]);
      setLoadingMessages(false);
      return undefined;
    }
    setLoadingMessages(true);
    const messagesQuery = query(
      collection(db, "consultationChats", bookingId, "messages"),
      orderBy("timestamp", "asc"),
    );
    const unsub = onSnapshot(
      messagesQuery,
      (snapshot) => {
        const rows = snapshot.docs.map((item) => ({ id: item.id, ...item.data() }));
        setMessages(rows);
        setLoadingMessages(false);
      },
      (error) => {
        setLoadError(error.message || "Unable to load consultation messages.");
        setMessages([]);
        setLoadingMessages(false);
      },
    );
    return () => unsub();
  }, [bookingId]);

  // CONSULTATION CHAT: Vet left panel list for active/waiting consultations.
  useEffect(() => {
    if (userRole !== "vet" || !currentUserUid) {
      setVetConsultations([]);
      return undefined;
    }
    const vetChatsQuery = query(
      collection(db, "consultationChats"),
      where("providerUid", "==", currentUserUid),
    );
    const unsub = onSnapshot(
      vetChatsQuery,
      (snapshot) => {
        const rows = snapshot.docs
          .map((item) => ({ id: item.id, ...item.data() }))
          .filter((row) => ["waiting", "active"].includes(String(row.status || "").toLowerCase()))
          .sort((a, b) => {
            const aTs = a?.lastMessageAt?.seconds || 0;
            const bTs = b?.lastMessageAt?.seconds || 0;
            return bTs - aTs;
          });
        setVetConsultations(rows);
      },
      () => {
        setVetConsultations([]);
      },
    );
    return () => unsub();
  }, [currentUserUid, userRole]);

  // CONSULTATION CHAT: Auto-scroll to latest message on updates.
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages]);

  // CONSULTATION CHAT: Mark incoming unread messages as read when visible.
  useEffect(() => {
    if (!bookingId) return;
    if (!messages.length) return;
    if (document.visibilityState !== "visible") return;
    if (readSyncInFlightRef.current) return;

    const unreadIncoming = messages
      .filter((item) => {
        const senderRole = String(item.senderRole || "").toLowerCase();
        return senderRole && senderRole !== userRole && item.read !== true;
      })
      .map((item) => item.id)
      .slice(0, 200);

    if (!unreadIncoming.length) return;

    readSyncInFlightRef.current = true;
    void callChatApi("mark_read", { messageIds: unreadIncoming })
      .catch(() => {})
      .finally(() => {
        readSyncInFlightRef.current = false;
      });
  }, [bookingId, messages, userRole, callChatApi]);

  // CONSULTATION CHAT: Derived participant header values without exposing private contact info.
  const headerData = useMemo(() => {
    const effectiveStatus = String(
      chatDoc?.status || chatStatusDetails?.status || "waiting",
    ).toLowerCase();

    if (userRole === "owner") {
      return {
        title: String(chatDoc?.assignedVetName || chatStatusDetails?.assignedVetName || "Verified Petaar Vet"),
        subtitle: "Verified Petaar Vet",
        status: effectiveStatus,
      };
    }

    return {
      title: "Pet Owner",
      subtitle: [
        `Pet: ${String(chatDoc?.ownerPetName || chatStatusDetails?.ownerPetName || "Pet")}`,
        String(chatDoc?.ownerPetSpecies || chatStatusDetails?.petDetails?.species || ""),
      ].filter(Boolean).join(" | "),
      status: effectiveStatus,
    };
  }, [chatDoc, chatStatusDetails, userRole]);

  const canSendText = Boolean(
    bookingId &&
    draft.trim() &&
    !sending &&
    !uploadingImage &&
    headerData.status !== "closed",
  );
  const consultationMode = normalizeConsultationMode(chatStatusDetails?.consultationMode || "");
  const hasVetStartedChat = useMemo(
    () => messages.some((item) => String(item.senderRole || "").toLowerCase() === "vet"),
    [messages],
  );
  const ownerCanStartCall = Boolean(
    userRole === "owner" &&
    headerData.status === "active" &&
    hasVetStartedChat &&
    (consultationMode === "audio" || consultationMode === "video"),
  );

  const openConsultationRoute = useCallback((nextBookingId) => {
    const safeId = String(nextBookingId || "").trim();
    if (!safeId) return;
    window.location.hash = `#/consultation/${encodeURIComponent(safeId)}`;
    setShowMobileList(false);
  }, []);

  // CONSULTATION CHAT: Text message sender with client-side personal-info scrubbing.
  const sendTextMessage = useCallback(async () => {
    const raw = String(draft || "").trim();
    if (!raw || !canSendText) return;
    try {
      setSending(true);
      const filtered = filterPersonalInfo(raw);
      setComposerWarning(filtered.blocked ? BLOCK_WARNING_TEXT : "");
      const result = await callChatApi("send_message", {
        content: filtered.filteredText,
        messageType: "text",
        clientBlocked: filtered.blocked,
      });
      if (result?.blocked) {
        setComposerWarning(BLOCK_WARNING_TEXT);
      }
      setDraft("");
    } catch (error) {
      setLoadError(error.message || "Unable to send message.");
    } finally {
      setSending(false);
    }
  }, [callChatApi, canSendText, draft]);

  // CONSULTATION CHAT: Owner image sender using base64 data URL payload.
  const onSelectImage = useCallback(async (event) => {
    const file = event?.target?.files?.[0];
    event.target.value = "";
    if (!file || userRole !== "owner") return;
    if (headerData.status === "closed") return;
    try {
      setUploadingImage(true);
      const optimizedFile = await optimizeImageForUpload(file, {
        maxWidth: 1280,
        maxHeight: 1280,
        maxOutputBytes: 420 * 1024,
        outputType: "image/jpeg",
        quality: 0.78,
      });
      const dataUrl = await fileToDataUrl(optimizedFile || file);
      if (!dataUrl.startsWith("data:image/")) {
        throw new Error("Unsupported image format.");
      }
      if (dataUrl.length > IMAGE_MAX_DATA_URL_CHARS) {
        throw new Error("Image is too large. Please use a smaller photo.");
      }
      await callChatApi("send_message", {
        content: dataUrl,
        messageType: "image",
      });
      setComposerWarning("");
    } catch (error) {
      setLoadError(error.message || "Unable to send image.");
    } finally {
      setUploadingImage(false);
    }
  }, [callChatApi, headerData.status, userRole]);

  // CONSULTATION CHAT: Vet prescription message sender.
  const sharePrescription = useCallback(async () => {
    if (userRole !== "vet" || headerData.status === "closed") return;
    try {
      setSending(true);
      await callChatApi("send_message", {
        messageType: "prescription",
        content: "Prescription shared by your vet. Open Bookings to view and download.",
      });
    } catch (error) {
      setLoadError(error.message || "Unable to share prescription.");
    } finally {
      setSending(false);
    }
  }, [callChatApi, headerData.status, userRole]);

  // CONSULTATION CHAT: Vet/admin consultation closing action.
  const closeConsultation = useCallback(async () => {
    if (userRole !== "vet" || headerData.status === "closed") return;
    try {
      setClosingConsultation(true);
      await callChatApi("close_consultation");
    } catch (error) {
      setLoadError(error.message || "Unable to close consultation.");
    } finally {
      setClosingConsultation(false);
    }
  }, [callChatApi, headerData.status, userRole]);

  const startSecureCall = useCallback(async () => {
    if (!ownerCanStartCall || joiningCall) return;
    try {
      setJoiningCall(true);
      await openConsultationCallWindow({
        id: bookingId,
        bookingType: "vet_consultation",
        consultationMode,
      });
    } catch (error) {
      setLoadError(error.message || "Unable to start secure call.");
    } finally {
      setJoiningCall(false);
    }
  }, [bookingId, consultationMode, joiningCall, ownerCanStartCall]);

  const statusText = headerData.status === "active" ? "🟢 Vet is online" :
    headerData.status === "closed" ? "⚪ Consultation ended" : "🟡 Connecting...";

  return (
    <div className={`consultation-chat-root${showMobileList ? " show-list" : ""}`}>
      {userRole === "vet" ? (
        <aside className="consultation-chat-left">
          <div className="consultation-chat-left-header">Active Consultations</div>
          <div className="consultation-chat-list">
            {vetConsultations.length ? vetConsultations.map((row, index) => {
              const isCurrent = String(row.bookingId || row.id) === bookingId;
              return (
                <div
                  key={row.id}
                  className={`consultation-chat-list-item${isCurrent ? " active" : ""}`}
                  onClick={() => openConsultationRoute(row.bookingId || row.id)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      openConsultationRoute(row.bookingId || row.id);
                    }
                  }}
                >
                  <div className="consultation-chat-list-label">
                    <span>Pet Owner #{index + 1}</span>
                    <span>{formatTimeAgo(row.lastMessageAt)}</span>
                  </div>
                  <div className="consultation-chat-list-meta">
                    {(row.ownerPetSpecies || "Pet").slice(0, 32)} • {(row.lastMessagePreview || "No messages yet").slice(0, 56)}
                  </div>
                </div>
              );
            }) : (
              <div className="consultation-chat-list-item">No active consultation chats.</div>
            )}
          </div>
        </aside>
      ) : null}

      <section className="consultation-chat-main">
        <header className="consultation-chat-header">
          <div>
            <div className="consultation-chat-header-title">
              {userRole === "vet" ? (
                <button
                  type="button"
                  className="consultation-chat-btn consultation-chat-mobile-back"
                  onClick={() => setShowMobileList(true)}
                >
                  Back
                </button>
              ) : null}
              {headerData.title}
            </div>
            <div className="consultation-chat-header-sub">{headerData.subtitle || "Consultation chat"}</div>
          </div>
          {userRole === "owner" ? (
            <span className="consultation-chat-badge">Verified Petaar Vet</span>
          ) : null}
        </header>

        <div className="consultation-chat-messages">
          {loadError ? (
            <div className="consultation-chat-warning">{loadError}</div>
          ) : null}

          {loadingMessages ? (
            <div className="consultation-chat-system">Loading consultation chat...</div>
          ) : null}

          {!loadingMessages && !messages.length ? (
            <div className="consultation-chat-system">No messages yet. Start the consultation chat.</div>
          ) : null}

          {messages.map((item) => {
            const senderRole = String(item.senderRole || "").toLowerCase();
            const messageType = String(item.messageType || "text").toLowerCase();
            const mine = senderRole === userRole;
            const isSystem = senderRole === "system" || messageType === "system_alert";

            if (isSystem) {
              return (
                <div key={item.id} className="consultation-chat-system">
                  {String(item.content || "")}
                </div>
              );
            }

            return (
              <div key={item.id} className={`consultation-chat-row ${mine ? "mine" : "other"}`}>
                <div className={`consultation-chat-bubble ${senderRole === "owner" ? "owner" : "vet"}`}>
                  {messageType === "image" ? (
                    <img
                      src={String(item.content || "")}
                      alt="Pet update"
                      style={{ width: "100%", maxWidth: 280, borderRadius: 10, display: "block" }}
                    />
                  ) : null}

                  {messageType === "prescription" ? (
                    <div className="consultation-chat-prescription">
                      <div className="consultation-chat-prescription-title">💊 Prescription Shared</div>
                      <div style={{ marginBottom: 8 }}>{String(item.content || "")}</div>
                      <button
                        type="button"
                        className="consultation-chat-prescription-btn"
                        onClick={() => {
                          window.location.hash = userRole === "vet" ?
                            "#/page/provider-bookings" :
                            "#/page/owner-bookings";
                        }}
                      >
                        View Prescription
                      </button>
                    </div>
                  ) : null}

                  {messageType === "text" ? (
                    <div style={{ whiteSpace: "pre-wrap" }}>{String(item.content || "")}</div>
                  ) : null}

                  <div className="consultation-chat-meta">
                    {mine ? "You" : String(item.senderDisplayName || (senderRole === "vet" ? "Vet" : "Pet Owner"))}
                    {" • "}
                    {formatMessageTime(item.timestamp)}
                  </div>

                  {item.originalBlocked ? (
                    <div className="consultation-chat-warning">{BLOCK_WARNING_TEXT}</div>
                  ) : null}
                </div>
              </div>
            );
          })}
          <div ref={messagesEndRef} />
        </div>

        <div className="consultation-chat-input">
          {composerWarning ? (
            <div className="consultation-chat-warning">{composerWarning}</div>
          ) : null}

          <textarea
            className="consultation-chat-textarea"
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            maxLength={4000}
            placeholder="Type your message here"
            disabled={headerData.status === "closed" || sending}
          />

          <div className="consultation-chat-input-row">
            {userRole === "owner" ? (
              <>
                <input
                  ref={imageInputRef}
                  type="file"
                  accept="image/*"
                  style={{ display: "none" }}
                  onChange={onSelectImage}
                />
                <button
                  type="button"
                  className="consultation-chat-btn"
                  onClick={() => imageInputRef.current?.click()}
                  disabled={uploadingImage || headerData.status === "closed"}
                >
                  {uploadingImage ? "Uploading..." : "Attach Image"}
                </button>
              </>
            ) : null}

            {userRole === "vet" ? (
              <button
                type="button"
                className="consultation-chat-btn success"
                onClick={sharePrescription}
                disabled={sending || headerData.status === "closed"}
              >
                Share Prescription
              </button>
            ) : null}

            <button
              type="button"
              className="consultation-chat-btn primary"
              onClick={sendTextMessage}
              disabled={!canSendText}
            >
              {sending ? "Sending..." : "Send"}
            </button>
          </div>
        </div>

        <div className="consultation-chat-statusbar">
          <span>{statusText}</span>
          {ownerCanStartCall ? (
            <button
              type="button"
              className="consultation-chat-btn success"
              onClick={startSecureCall}
              disabled={joiningCall}
            >
              {joiningCall ?
                "Opening..." :
                `Start Secure ${getConsultationJoinLabel(consultationMode)}`}
            </button>
          ) : null}
          {userRole === "vet" ? (
            <button
              type="button"
              className="consultation-chat-btn danger"
              onClick={closeConsultation}
              disabled={closingConsultation || headerData.status === "closed"}
            >
              {closingConsultation ? "Ending..." : "End Consultation"}
            </button>
          ) : null}
        </div>
      </section>
    </div>
  );
}
